// SESSION-TIMELINE (A19, docs/design/2026-10-31-consolidate-then-demo.md): the snapshots strip
// under the Live workspace. One snapshot per canvas op (the state rebuilt from the op log, cut to
// what the viewer may read), next to what was said in the session's transcript threads at that
// moment, the files the session showed, and the versions of a block shown more than once. Read
// only; feedback on a snapshot is an ordinary comment on the card (`messages.create` on the
// task's thread) or on the session thread, so it needs no route of its own.
import { z } from "zod";
import { Timestamp, Uuid } from "../primitives";
import { CanvasBlock, CanvasHighlight, CanvasLayout, CanvasWindowGeometry } from "./canvas";
import { CompanyParams } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";

export const SessionTimelineQuery = z.object({ session_id: Uuid });

/** One line said in a transcript thread the viewer may read. */
export const TimelineSaid = z
  .object({
    message_id: Uuid,
    thread_id: Uuid,
    at: Timestamp,
    author_kind: z.string(),
    author_membership_id: Uuid.nullable(),
    text: z.string(),
  })
  .meta({ id: "TimelineSaid" });

/** Where feedback on a snapshot goes: the card it is about, else the session thread, else nowhere. */
export const TimelineTarget = z
  .object({ kind: z.enum(["task", "thread"]), id: Uuid })
  .meta({ id: "TimelineTarget" });

export const TimelineSnapshot = z
  .object({
    index: z.number().int(),
    op_id: Uuid,
    at: Timestamp,
    verb: z.enum(["show", "arrange", "highlight", "clear", "tidy"]),
    /** One line: what this op did. */
    summary: z.string(),
    blocks: z.array(CanvasBlock),
    layout: CanvasLayout,
    highlight: CanvasHighlight.nullable(),
    /** LIVE-DESKTOP: the desk's geometry then (the strip draws a mini-desk from it). */
    windows: z.record(z.string(), CanvasWindowGeometry).optional(),
    said: z.array(TimelineSaid),
    target: TimelineTarget.nullable(),
  })
  .meta({ id: "TimelineSnapshot" });

export const TimelineFile = z
  .object({
    id: Uuid,
    name: z.string(),
    mime: z.string(),
    task_id: Uuid,
    at: Timestamp,
    /** The first snapshot that showed the file's block. */
    snapshot_index: z.number().int(),
  })
  .meta({ id: "TimelineFile" });

export const TimelineVersion = z.object({
  version: z.number().int(),
  snapshot_index: z.number().int(),
  at: Timestamp,
  block: CanvasBlock,
  /** An image grid's files as they stood when this version was shown. */
  file_ids: z.array(Uuid),
});

/** A block shown more than once: its versions in order (v1, v2, ...). */
export const TimelineBlockVersions = z
  .object({ block_id: z.string(), versions: z.array(TimelineVersion).min(2) })
  .meta({ id: "TimelineBlockVersions" });

export const SessionTimeline = z
  .object({
    session_id: Uuid,
    snapshots: z.array(TimelineSnapshot),
    files: z.array(TimelineFile),
    versions: z.array(TimelineBlockVersions),
    /** The session thread: the first transcript thread the viewer may read, or null. */
    session_thread_id: Uuid.nullable(),
  })
  .meta({ id: "SessionTimeline" });
export type SessionTimeline = z.infer<typeof SessionTimeline>;

export const canvasTimelineRoutes = {
  "liveCanvas.timeline": defineRoute({
    feature: "core",
    id: "liveCanvas.timeline",
    method: "get",
    path: `${base}/live/canvas/timeline`,
    tag: "live",
    summary: "The Live session's snapshots strip: every canvas state with what was said",
    description:
      "One snapshot per canvas op of this session and the company-wide ones (the newest 500 of " +
      "the last 24 hours), each state rebuilt from the op log and cut to the blocks the caller " +
      "may read; the lines said in the transcript threads it may read, placed at the snapshot " +
      "in force when they were said; the files its image grids showed; and the versions of a " +
      "block shown more than once. 404 while the company's `live_canvas` switch is off.",
    access: "viewer",
    params: CompanyParams,
    query: SessionTimelineQuery,
    response: { status: 200, schema: SessionTimeline },
    errors: [],
    emits: [],
  }),
} as const;
